import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import Breadcrumbs from '@/components/navigation/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Plus, 
  Building2, 
  Car, 
  Package, 
  MoreVertical,
  Pencil,
  Trash2,
  Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESOURCE_TYPES = [
  { value: 'room', label: 'Ruimte', icon: Building2 },
  { value: 'vehicle', label: 'Voertuig', icon: Car },
  { value: 'equipment', label: 'Apparatuur', icon: Package },
  { value: 'other', label: 'Anders', icon: Package },
];

const COLORS = [
  '#0d9488', '#3b82f6', '#8b5cf6', '#ec4899', 
  '#f59e0b', '#ef4444', '#10b981', '#6366f1'
];

const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'room',
    description: '',
    capacity: '',
    color: '#0d9488',
    hourly_rate: '',
    is_active: true
  });

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources`);
      setResources(response.data);
    } catch (error) {
      toast.error('Kon resources niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Naam is verplicht');
      return;
    }

    try {
      const data = {
        ...formData,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null
      };

      if (editingResource) {
        await axios.put(`${API}/resources/${editingResource.id}`, data);
        toast.success('Resource bijgewerkt');
      } else {
        await axios.post(`${API}/resources`, data);
        toast.success('Resource toegevoegd');
      }

      setDialogOpen(false);
      resetForm();
      fetchResources();
    } catch (error) {
      toast.error('Actie mislukt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze resource wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API}/resources/${id}`);
      toast.success('Resource verwijderd');
      fetchResources();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const openEditDialog = (resource) => {
    setEditingResource(resource);
    setFormData({
      name: resource.name,
      type: resource.type,
      description: resource.description || '',
      capacity: resource.capacity?.toString() || '',
      color: resource.color || '#0d9488',
      hourly_rate: resource.hourly_rate?.toString() || '',
      is_active: resource.is_active
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingResource(null);
    setFormData({
      name: '',
      type: 'room',
      description: '',
      capacity: '',
      color: '#0d9488',
      hourly_rate: '',
      is_active: true
    });
  };

  const getTypeIcon = (type) => {
    const typeConfig = RESOURCE_TYPES.find(t => t.value === type);
    return typeConfig?.icon || Package;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-slate-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="resources-page">
        <Breadcrumbs items={[{ label: 'Reserveringen', href: '/bookings' }, { label: 'Resources' }]} />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Resources</h1>
            <p className="text-slate-500">Beheer ruimtes, apparatuur en meer</p>
          </div>
          <Button 
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
            data-testid="add-resource-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Resource toevoegen
          </Button>
        </div>

        {/* Resources Grid */}
        {resources.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {resources.map((resource) => {
              const TypeIcon = getTypeIcon(resource.type);
              return (
                <Card 
                  key={resource.id} 
                  className={`border-0 shadow-sm hover:shadow-md transition-all ${!resource.is_active ? 'opacity-60' : ''}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${resource.color}20` }}
                      >
                        <TypeIcon 
                          className="w-6 h-6"
                          style={{ color: resource.color }}
                        />
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(resource)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Bewerken
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(resource.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Verwijderen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">{resource.name}</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      {RESOURCE_TYPES.find(t => t.value === resource.type)?.label || 'Resource'}
                    </p>
                    
                    {resource.description && (
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">{resource.description}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      {resource.capacity && (
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>{resource.capacity} personen</span>
                        </div>
                      )}
                      {resource.hourly_rate && (
                        <span>€{resource.hourly_rate}/uur</span>
                      )}
                    </div>
                    
                    {!resource.is_active && (
                      <span className="inline-block mt-3 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                        Inactief
                      </span>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Geen resources</h3>
              <p className="text-slate-500 mb-4">
                Voeg ruimtes, apparatuur of andere resources toe om te beginnen met plannen
              </p>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Eerste resource toevoegen
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingResource ? 'Resource bewerken' : 'Resource toevoegen'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Naam *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Bijv. Vergaderzaal A"
                  className="rounded-xl"
                  data-testid="resource-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RESOURCE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          <span>{type.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Beschrijving</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optionele beschrijving..."
                  className="rounded-xl resize-none"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="capacity">Capaciteit</Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                    placeholder="Aantal personen"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hourly_rate">Uurtarief (€)</Label>
                  <Input
                    id="hourly_rate"
                    type="number"
                    step="0.01"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                    placeholder="0.00"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Kleur</Label>
                <div className="flex gap-2">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full transition-transform ${
                        formData.color === color ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="is_active">Actief</Label>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>

              <DialogFooter className="gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  className="rounded-xl"
                >
                  Annuleren
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                  data-testid="save-resource-btn"
                >
                  {editingResource ? 'Opslaan' : 'Toevoegen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ResourcesPage;
